import type { GroupField } from "@/util/methods";
import { isLocationCfV1Whitelisted } from "@/util/customFieldsPhase1";
import type { AssetType, BuilderContext, ContextElement } from "./types";

/**
 * The slice of the app store the context builder reads. Declared structurally
 * rather than importing the store type so the builder stays testable with a
 * plain object and does not drag Pinia into every rule test.
 */
export interface ContextSource {
  formId?: string | null;
  surveyId?: string | null;
  quizId?: string | null;
  locationId?: string;
  elements?: GroupField[];
  slides?: Array<{ id?: string; slideData?: GroupField[] }>;
  categoryCustomFields?: GroupField[];
  builderConfig?: Record<string, unknown>;
  formAction?: Record<string, unknown>;
  payment?: Record<string, unknown>;
  addedProductList?: Array<Record<string, unknown>>;
  bumProductList?: Array<Record<string, unknown>>;
  category?: Array<Record<string, unknown>>;
  resultTemplate?: Record<string, unknown> | null;
  /** Labs opt-in half of the Custom Fields Phase 1 rollout. */
  isNewCustomFieldsEnabled?: boolean;
  emailNotificationsConfig?: Record<string, unknown> | null;
  autoResponderConfig?: Record<string, unknown> | null;
  opportunitySettings?: Record<string, unknown> | null;
  footerStyle?: Record<string, unknown>;
  customFields?: Array<Record<string, unknown>>;
  enableSaveButton?: boolean;
  checkOpportunitiesFieldExists?: () => boolean;
  checkSnapshotTransferIsValid?: () => unknown;
}

/**
 * Identity for an element, used to track a finding across edits.
 *
 * Position is deliberately the last resort: inserting, moving or deleting an
 * element shifts every index after it, and a key built on position would make
 * a standing finding follow the wrong element. Anything with a real identity
 * (a custom-field id, a uuid, a field or query key) keeps the same key wherever
 * it moves.
 *
 * Elements that may legitimately appear more than once — text, html, image —
 * share a query key and carry no id on forms, so they still fall back to
 * position and a finding on one of those clears when it moves.
 */
export const elementKeyFor = (
  element: GroupField,
  elementIndex: number,
  slideIndex?: number
): string => {
  const stable =
    element?.id ||
    (element as { uuid?: string })?.uuid ||
    element?.fieldKey ||
    element?.hiddenFieldQueryKey;
  if (stable) return `id:${stable}`;

  const scope = slideIndex === undefined ? "" : `${slideIndex}:`;
  return `pos:${scope}${element?.tag}:${elementIndex}`;
};

export const assetTypeOf = (source: ContextSource): AssetType => {
  if (source.quizId) return "quiz";
  if (source.surveyId) return "survey";
  return "form";
};

/**
 * Flattens the asset into one list of positioned elements.
 *
 * Forms keep a flat `elements` array; surveys and quizzes nest elements inside
 * slides. Quizzes additionally carry `categoryCustomFields`, which are real
 * fields that save with the asset and so must be validated, but belong to no
 * slide — they get `slideIndex: undefined`, like form elements.
 */
const flatten = (
  source: ContextSource,
  assetType: AssetType
): { elements: ContextElement[]; slides: ContextElement[][] } => {
  const elements: ContextElement[] = [];
  const slides: ContextElement[][] = [];

  if (assetType === "form") {
    (source.elements ?? []).forEach((element, elementIndex) => {
      elements.push({
        element,
        elementIndex,
        elementKey: elementKeyFor(element, elementIndex),
      });
    });
  } else {
    (source.slides ?? []).forEach((slide, slideIndex) => {
      const slideElements: ContextElement[] = [];
      (slide?.slideData ?? []).forEach((element, elementIndex) => {
        const entry: ContextElement = {
          element,
          elementIndex,
          slideIndex,
          elementKey: elementKeyFor(element, elementIndex, slideIndex),
        };
        slideElements.push(entry);
        elements.push(entry);
      });
      slides.push(slideElements);
    });
  }

  (source.categoryCustomFields ?? []).forEach((element, elementIndex) => {
    elements.push({
      element,
      elementIndex,
      elementKey: elementKeyFor(element, elementIndex),
    });
  });

  return { elements, slides };
};

/**
 * Builds the snapshot every rule reads.
 *
 * Deliberately synchronous: anything needing a network call (location custom
 * fields, connected payment providers) is already resolved on the store by the
 * time save runs, so a validation pass never introduces a request of its own.
 */
export const buildContext = (source: ContextSource): BuilderContext => {
  const assetType = assetTypeOf(source);
  const { elements, slides } = flatten(source, assetType);

  return {
    assetType,
    locationId: source.locationId ?? "",
    elements,
    slides,
    slideCount: slides.length,
    slideIds: (source.slides ?? [])
      .map((slide) => String(slide?.id ?? "").trim())
      .filter(Boolean),
    builderConfig: source.builderConfig ?? {},
    formAction: source.formAction ?? {},
    payment: source.payment ?? {},
    addedProducts: source.addedProductList ?? [],
    bumpProducts: source.bumProductList ?? [],
    categories: source.category ?? [],
    resultTemplate: source.resultTemplate ?? null,
    emailNotificationsConfig: source.emailNotificationsConfig ?? null,
    autoResponderConfig: source.autoResponderConfig ?? null,
    opportunitySettings: source.opportunitySettings ?? null,
    footerStyle: source.footerStyle ?? {},
    locationCustomFields: source.customFields ?? [],
    // Mirrors isCfV1Enabled, which is an OR: the controls render for Labs
    // opt-ins as well as allowlisted locations, so the gate that decides
    // whether their state blocks a save has to cover both or it fails open.
    dateTimeRolloutEnabled:
      isLocationCfV1Whitelisted(source.locationId ?? "") ||
      !!source.isNewCustomFieldsEnabled,
    hasOpportunityField: Boolean(source.checkOpportunitiesFieldExists?.()),
    pipelineStageValid: Boolean(source.checkSnapshotTransferIsValid?.()),
    saveEnabledByOtherSurfaces: source.enableSaveButton ?? true,
  };
};
